// @ts-check
/**
 * @typedef {import('@datahooks/types/tagging.types').TagValueRequest} TagValueRequest
 * @typedef {import('@datahooks/types/tagging.types').TagValueResponse} TagValueResponse
 */

import useAuthenticatedMutation from '../useAuthenticatedMutation';
import axios from 'axios';
import { API_ADMIN_URL, FUNCTION_KEY, PROJECT_SCOPE } from '@home/config';
import logError, { toError } from '@datahooks/metadata/ErrorLogging';

import PropTypes from 'prop-types';

/**
 * Performs the API call to manage a project tag using POST and DELETE methods.
 *
 * @param {{ data: TagValueRequest, method: string }} params - The payload and HTTP method.
 * @param {string} token - The authentication token provided by useAuthenticatedMutation.
 * @returns {Promise<TagValueResponse>} The API response, either a success or error object.
 */
const manageProjectTag = async ({ data, method }, token) => {
  let url = `${API_ADMIN_URL}project_tag_values`;

  try {
      const response = await axios({
          method: method,
          url: url,
          headers: {
              'Authorization': `Bearer ${token}`,
              'x-functions-key': FUNCTION_KEY,
          },
          data: method !== 'DELETE' ? data : undefined, // Send data in body for POST
          params: method === 'DELETE' ? { project_id: data?.project_id, tag_value_id: data?.tag_value_id } : undefined,  // Send data as query params for DELETE, body for others
      });

      return response.data;
  } catch (error) {
      logError(`Failed to ${method.toLowerCase()} project:`, error, {});
      throw (error?.response?.data) ? error.response.data : error;
  }
}

/**
 * Hook to manage project tags (add/remove)
 * 
 * @param {function} onSuccess 
 * @param {function} onError 
 * @returns 
 */
export default function useProjectManager(onSuccess, onError) {
    
    const projectScope = [PROJECT_SCOPE];
    const { mutate, isLoading, isError, error, data, reset: resetMutation } = useAuthenticatedMutation(manageProjectTag, projectScope, {
        onSuccess,
        onError
    });

    /**
     * Function to perform the action
     * 
     * @param {TagValueRequest} data 
     * @param {'POST'|'DELETE'} [method='POST'] 
     * @returns {Promise<TagValueResponse | Error>} The API response, either a success or error object.
     */
    const performAction = (data, method = 'POST') => {
      return new Promise((resolve, reject) => {
        mutate(
          { data, method },
          {
            onSuccess: (result) => {
              resolve(result);
              if (onSuccess) onSuccess(result);
            },
            onError: (err) => {
              const returnedError = toError(err);
              logError('Error performing coverage management action', returnedError, { data, method });
  
              const asError = returnedError instanceof Error ? returnedError : new Error(String(returnedError));  // Sonar S6671 satisfied - toError always returns an Error or stringifiable value but Sonar doesn't track that
              reject(asError);
              onError?.(asError);
            },
          }
        );
      });
    };

    return {
        performAction,
        isLoading,
        isError,
        error,
        data,
        reset: resetMutation
    };
}

useProjectManager.propTypes = {
    onSuccess: PropTypes.func,
    onError: PropTypes.func
}